import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/**
 * useDirtyExitGuard
 *
 * Reusable unsaved-progress exit guard for Dialog-based wizards.
 * Prevents accidental data loss on Escape / outside-click / X / Cancel.
 *
 * Usage:
 *   const guard = useDirtyExitGuard({
 *     isDirty: someComputedBool,
 *     onConfirmClose: () => setOpen(false),
 *     title: 'Exit recipe upload?',
 *     body: 'You have unsaved recipe upload progress...',
 *     testIdPrefix: 'recipe-upload-exit-confirm',
 *   });
 *
 *   <Dialog open={open} onOpenChange={(o) => !o && guard.requestClose()}>
 *     <DialogContent
 *       onEscapeKeyDown={guard.onEscapeKeyDown}
 *       onPointerDownOutside={guard.onPointerDownOutside}
 *       onInteractOutside={guard.onInteractOutside}
 *     >
 *       ...
 *       {guard.ConfirmDialog}
 *     </DialogContent>
 *   </Dialog>
 *
 * When the user completes the wizard intentionally (successful submit),
 * call `guard.allowNextClose()` BEFORE invoking onConfirmClose so the
 * latch bypasses the confirmation one time.
 */
export function useDirtyExitGuard({
  isDirty,
  onConfirmClose,
  title = 'Exit?',
  body = 'You have unsaved progress. If you exit now, your changes will be lost.',
  continueLabel = 'Continue editing',
  discardLabel = 'Exit and discard',
  testIdPrefix = 'dirty-exit-confirm',
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const allowNextCloseRef = useRef(false);

  const requestClose = useCallback(() => {
    if (allowNextCloseRef.current || !isDirty) {
      allowNextCloseRef.current = false;
      onConfirmClose?.();
      return;
    }
    setConfirmOpen(true);
  }, [isDirty, onConfirmClose]);

  const confirmDiscardAndExit = useCallback(() => {
    allowNextCloseRef.current = true;
    setConfirmOpen(false);
    onConfirmClose?.();
  }, [onConfirmClose]);

  // Allow one intentional close (e.g. successful submit) to bypass the guard.
  const allowNextClose = useCallback(() => {
    allowNextCloseRef.current = true;
  }, []);

  const onEscapeKeyDown = useCallback(
    (e) => {
      if (isDirty) {
        e.preventDefault();
        setConfirmOpen(true);
      }
    },
    [isDirty]
  );

  const onPointerDownOutside = useCallback(
    (e) => {
      if (isDirty) {
        e.preventDefault();
        setConfirmOpen(true);
      }
    },
    [isDirty]
  );

  const onInteractOutside = useCallback(
    (e) => {
      if (isDirty) {
        e.preventDefault();
      }
    },
    [isDirty]
  );

  const ConfirmDialog = useMemo(
    () => (
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent
          className="bg-[#18181b] border-[#27272a] text-white"
          data-testid={testIdPrefix}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{title}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-300">
              {body}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-zinc-700 hover:bg-zinc-600 text-white border-0"
              data-testid={`${testIdPrefix}-cancel`}
            >
              {continueLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDiscardAndExit}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid={`${testIdPrefix}-discard`}
            >
              {discardLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    ),
    [confirmOpen, title, body, continueLabel, discardLabel, testIdPrefix, confirmDiscardAndExit]
  );

  return {
    requestClose,
    allowNextClose,
    onEscapeKeyDown,
    onPointerDownOutside,
    onInteractOutside,
    ConfirmDialog,
  };
}
